import { type InfiniteData, useMutation, useQueryClient } from '@tanstack/react-query';
import { merge } from 'lodash';

import { type ShareUrlUpdatedSetting } from '@air/analytics';
import { type Asset, type ListResponse, type ShortUrlObjectType } from '@air/api/types';

import {
  getAssetShareLinkObjectId,
  getAssetShareLinkTargetClipId,
} from '~/components/SharePrivateClipModal/buildAssetShareLinkSlots';
import {
  mergeAssetShareLinkApiUpdatePatch,
  mergeAssetShareLinkUpdatePatch,
} from '~/components/SharePrivateClipModal/mergeAssetShareLinkUpdatePatch';
import { type AssetScopedShareUrlResponseWithSource } from '~/components/SharePrivateClipModal/types';
import { useRevalidateISR } from '~/hooks/useRevalidateISR';
import { useCurrentWorkspace } from '~/providers/CurrentWorkspaceProvider';
import {
  findAssetShareLinkInListCache,
  patchAssetShareLinksListQueryData,
} from '~/swr-hooks/shortUrl/updateAssetShareLinkInListCache';
import {
  type AssetScopedShareLinkListItem,
  getAssetShareLinksListKey,
} from '~/swr-hooks/shortUrl/useListAssetShareLinks';
import { type ShareUrlUpdateHandlerProps, useUpdateShortId } from '~/swr-hooks/shortUrl/useUpdateShortId';

/** List-row identity after an optional clip ↔ asset conversion. */
const getConversionListFields = (
  assetId: string,
  link: AssetScopedShareLinkListItem,
  objectType: ShortUrlObjectType.clip | ShortUrlObjectType.asset,
): Pick<AssetScopedShareUrlResponseWithSource, 'objectId' | 'objectType' | 'targetClipId'> => ({
  objectType,
  objectId: getAssetShareLinkObjectId(link, assetId, objectType),
  targetClipId: getAssetShareLinkTargetClipId(link, objectType),
});

export type UseUpdateAssetScopedShareLinkParams = {
  assetId: Asset['id'];
};

export type UpdateAssetScopedShareLinkVariables = {
  patch: ShareUrlUpdateHandlerProps;
  analytics?: ShareUrlUpdatedSetting;
  shortId: string;
};

export const useUpdateAssetScopedShareLink = ({ assetId }: UseUpdateAssetScopedShareLinkParams) => {
  const { currentWorkspace } = useCurrentWorkspace();
  const { updateShortId } = useUpdateShortId();
  const { revalidateISR } = useRevalidateISR();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      patch,
      analytics,
      shortId,
    }: UpdateAssetScopedShareLinkVariables): Promise<AssetScopedShareUrlResponseWithSource> => {
      const workspaceId = currentWorkspace?.id;

      if (!workspaceId || !shortId) {
        throw new Error('No share link for selected slot');
      }

      const shortIdInfo = findAssetShareLinkInListCache(queryClient, { workspaceId, assetId }, shortId);

      if (!shortIdInfo) {
        throw new Error('No share link for selected slot');
      }

      const objectType = patch.objectType ?? shortIdInfo.objectType;

      const response = await updateShortId.mutateAsync({
        objectId: getAssetShareLinkObjectId(shortIdInfo, assetId),
        boardContext: shortIdInfo.boardContext,
        updatePayload: {
          workspaceId,
          ...mergeAssetShareLinkApiUpdatePatch(shortIdInfo, patch),
          shortId,
        },
        analyticsPayload: analytics ? { objectType: shortIdInfo.objectType, setting: analytics } : undefined,
      });

      if (patch.objectType !== undefined) {
        await queryClient.invalidateQueries({
          queryKey: getAssetShareLinksListKey({ workspaceId, assetId }),
        });
      } else {
        patchAssetShareLinksListQueryData(queryClient, { workspaceId, assetId }, shortId, response);
      }

      /**
       * Don't await: ISR revalidation can be slow, and `revalidateISR` already reports
       * its own failures, so a rejection here cannot surface to the share-link save.
       * @see https://air-labs-team.slack.com/archives/CSU3B8B3R/p1699565392614499?thread_ts=1699564602.295969&cid=CSU3B8B3R
       */
      void revalidateISR({
        path: `/a/${shortId}`,
      });

      return {
        ...response,
        ...getConversionListFields(assetId, shortIdInfo, objectType),
      };
    },
    onMutate: async ({ patch, shortId }) => {
      const workspaceId = currentWorkspace?.id;

      if (!workspaceId || !shortId) {
        return;
      }

      const listKey = getAssetShareLinksListKey({ workspaceId, assetId });

      await queryClient.cancelQueries({ queryKey: listKey });

      const previousList = queryClient.getQueryData<InfiniteData<ListResponse<AssetScopedShareLinkListItem>>>(listKey);
      const shortIdInfo = findAssetShareLinkInListCache(queryClient, { workspaceId, assetId }, shortId);

      if (!shortIdInfo) {
        return;
      }

      const objectType = patch.objectType ?? shortIdInfo.objectType;

      patchAssetShareLinksListQueryData(
        queryClient,
        { workspaceId, assetId },
        shortId,
        merge(
          {},
          mergeAssetShareLinkUpdatePatch(shortIdInfo, patch),
          getConversionListFields(assetId, shortIdInfo, objectType),
        ),
      );

      return { previousList };
    },
    onError: (_error, _variables, context) => {
      const workspaceId = currentWorkspace?.id;

      if (!workspaceId || !context?.previousList) {
        return;
      }

      queryClient.setQueryData(getAssetShareLinksListKey({ workspaceId, assetId }), context.previousList);
    },
  });
};
